import React from 'react';
import styled, { css } from 'styled-components';
import { aircraftFly, withMotion } from '../styles/animations';

/*
 * Realistic commercial aircraft silhouette (side profile, nose to the right).
 * Animates diagonally across the background on a simulated descent path.
 * Respects prefers-reduced-motion: hidden entirely when motion is disabled.
 */

const AircraftSVG = () => (
  <svg
    viewBox="0 0 340 70"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
  >
    {/* ── Contrail (trails behind = to the left) ── */}
    <defs>
      <linearGradient id="trail" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" stopColor="white" stopOpacity="0" />
        <stop offset="85%" stopColor="white" stopOpacity="0.18" />
        <stop offset="100%" stopColor="white" stopOpacity="0.06" />
      </linearGradient>
    </defs>
    <rect x="0" y="29" width="190" height="2" rx="1" fill="url(#trail)" />
    <rect x="10" y="27" width="155" height="1" rx="0.5" fill="url(#trail)" opacity="0.5" />

    {/* ── Fuselage ── */}
    <path
      d="M200,26 Q225,24 248,25 Q268,26 282,29 Q290,31 294,33
         Q290,35 282,37 Q268,38 248,39 Q225,40 200,38
         L172,38 Q155,38 142,36 Q132,34 120,34
         L120,30 Q132,30 142,28 Q155,26 172,26 Z"
      fill="rgba(190,215,255,0.75)"
    />

    {/* ── Cockpit windows ── */}
    <ellipse cx="280" cy="33" rx="9" ry="3.5" fill="rgba(100,160,255,0.5)" />

    {/* ── Main wings (swept back) ── */}
    {/* Upper surface */}
    <polygon
      points="230,27 195,14 175,17 215,30"
      fill="rgba(165,200,250,0.7)"
    />
    {/* Lower surface */}
    <polygon
      points="230,37 195,50 175,47 215,34"
      fill="rgba(165,200,250,0.65)"
    />

    {/* ── Engine nacelle (left wing, slightly below) ── */}
    <rect x="192" y="38" width="28" height="10" rx="5" fill="rgba(130,170,220,0.65)" />
    {/* Engine intake highlight */}
    <ellipse cx="192" cy="43" rx="3" ry="4" fill="rgba(80,120,200,0.5)" />

    {/* ── Vertical tail (fin) ── */}
    <polygon
      points="130,26 140,8 152,26"
      fill="rgba(165,200,250,0.7)"
    />

    {/* ── Horizontal stabilizers ── */}
    {/* Upper */}
    <polygon
      points="130,27 108,19 105,23 130,30"
      fill="rgba(165,200,250,0.65)"
    />
    {/* Lower */}
    <polygon
      points="130,37 108,45 105,41 130,34"
      fill="rgba(165,200,250,0.65)"
    />

    {/* ── Subtle fuselage highlight ── */}
    <path
      d="M200,27 Q230,26 260,27 Q276,28 286,31"
      stroke="rgba(220,235,255,0.25)"
      strokeWidth="1.5"
      fill="none"
    />
  </svg>
);

const AircraftAnimation = () => (
  <Wrapper aria-hidden="true">
    <Aircraft>
      <AircraftSVG />
    </Aircraft>
  </Wrapper>
);

const Wrapper = styled.div`
  position: fixed;
  top: 12%;
  left: 0;
  width: 100%;
  height: 120px;
  z-index: 1;
  pointer-events: none;
  overflow: hidden;

  /* Hidden when reduced-motion is preferred */
  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const Aircraft = styled.div`
  position: absolute;
  top: 10px;
  left: 0;
  width: 240px;

  /* Static fallback — aircraft not visible if animation disabled */
  opacity: 0;

  ${withMotion(css`
    animation: ${aircraftFly} 28s ease-in-out infinite;
    animation-delay: 2s;
  `)}

  svg {
    width: 100%;
    height: auto;
    filter: drop-shadow(0 0 6px rgba(59,130,246,0.25));
  }
`;

export default AircraftAnimation;
